import type { StringsStructure } from '@george-labs.com/design-system';

import type { DeepRequired } from '@luna/utils-ts';
import { DateFormat } from '@luna/date';
import { t } from '@luna/i18n';
import { getFeatureConfig } from '@luna/feature-config';

export function getGdsTranslations(): DeepRequired<StringsStructure> {
    return {
        accountNumber: {
            mask: t('gds.accountNumber.mask_a11y'),
        },
        alert: {
            error: t('gds.alert.icon.error_a11y'),
            info: t('gds.alert.icon.info_a11y'),
            warning: t('gds.alert.icon.warning_a11y'),
        },
        amount: {
            currency: {},
            minus: t('gds.amount.minus_a11y'),
        },
        color: {
            berry: t('gds.color.berry'),
            carrot: t('gds.color.carrot'),
            forest: t('gds.color.forest'),
            honey: t('gds.color.honey'),
            indigo: t('gds.color.indigo'),
            lavender: t('gds.color.lavender'),
            lila: t('gds.color.lila'),
            lime: t('gds.color.lime'),
            pool: t('gds.color.pool'),
            rose: t('gds.color.rose'),
            stone: t('gds.color.stone'),
            teal: t('gds.color.teal'),
        },
        common: {
            clear: t('gds.clear_a11y'),
            close: t('app.close'),
            collapse: t('app.collapse'),
            expand: t('app.expand'),
            externalLink: t('gds.externalLink_a11y'),
            help: t('app.help'),
            more: t('gds.more_a11y'),
            previous: t('gds.prevPage_a11y'),
            next: t('gds.nextPage_a11y'),
            page: t('gds.pageNumber_a11y'),
        },
        dropdown: {
            selectYear: t('gds.dropdown.selectYear'),
        },
        fixed: {
            fixedButton: {
                backToTop: t('gds.fixedButton.backToTop'),
            },
        },
        form: {
            label: {
                optional: t('app.optional'),
            },
        },
        format: {
            date: getFeatureConfig((config) => config.base.defaultDateFormat),
            isoDate: DateFormat.ISO_DATE_FORMAT,
            screenreaderDate: getFeatureConfig((config) => config.base.defaultDateFormatVerbose),
        },
        input: {
            date: {
                button: t('app.dateButton_a11y'),
                clear: t('gds.dateClear_a11y'),
                nextMonth: t('gds.nextMonth_a11y'),
                previousMonth: t('gds.prevMonth_a11y'),
                nextYear: t('input.date.nextYear'),
                previousYear: t('input.date.previousYear'),
                rangeButton: t('gds.dateRangeButton_a11y'),
            },
            file: {
                browse: t('app.browseFiles'),
                delete: t('app.delete'),
                drag: t('app.dragFiles'),
                drop: t('app.dropFiles'),
                uploading: t('app.uploading'),
            },
            select: {
                selectAll: t('app.selectAll'),
                selectedItems: t('app.selectedItems', { skipInterpolation: true }),
            },
            productSelect: {
                selectAll: t('app.selectAllProducts'),
                selectedItems: t('app.selectedProducts', { skipInterpolation: true }),
            },
            time: {
                hoursAnnouncement: t('gds.hoursAnnouncement_a11y'),
                minutesAnnouncement: t('gds.minutesAnnouncement_a11y'),
            },
        },
        modal: {
            handle: t('gds.modal.handle_a11y'),
        },
        page: {
            notifications: {
                title: t('gds.page.notifications.title_a11y'),
                empty: t('gds.page.notifications.empty_a11y'),
            },
        },
        spinner: {
            message: t('app.loading'),
        },
        stepper: {
            compactInfo: t('gds.stepper.compactInfo', { skipInterpolation: true }),
        },
        table: {
            sort: {
                ascending: t('gds.table.sort.ascending_a11y'),
                column: t('gds.table.sort.column_a11y'),
                descending: t('gds.table.sort.descending_a11y'),
                select: t('gds.table.sort.select_a11y'),
            },
        },
        transaction: {
            format: {
                date: getFeatureConfig((config) => config?.base?.defaultDateFormat),
                largeDate: getFeatureConfig((config) => config?.base?.defaultDateFormatShort),

                largeDateYear: DateFormat.YEAR_LONG,
            },
            header: {
                amount: t('app.transactionGroup.columns.amount_a11y'),
                amountSecondary: t('app.transactionGroup.columns.amountSecondary_a11y'),
                avatar: t('app.transactionGroup.columns.type_a11y'),
                badges: t('app.transactionGroup.columns.categoriesOrState_a11y'),
                date: t('app.transactionGroup.columns.date_a11y'),
                detail: t('app.transactionGroup.columns.details_a11y'),
                indicators: t('app.transactionGroup.columns.indicators_a11y'),
                info: t('app.transactionGroup.columns.description_a11y'),
                infoSecondary: t('app.transactionGroup.columns.infoSecondary_a11y'),
                menu: t('app.transactionGroup.columns.menu_a11y'),
                misc: t('app.transactionGroup.columns.additionalInformation_a11y'),
                overlay: t('app.transactionGroup.columns.actions_a11y'),
                selectable: t('app.transactionGroup.columns.selectable_a11y'),
                statement: t('app.transactionGroup.columns.statement_a11y'),
                title: t('app.transactionGroup.columns.title_a11y'),
                titleSecondary: t('app.transactionGroup.columns.titleSecondary_a11y'),
                valuta: t('app.transactionGroup.columns.valuta_a11y'),
            },
            indicator: {
                unread: t('gds.transaction.indicator.unread_a11y'),
                star: t('gds.transaction.indicator.star_a11y'),
                attachment: t('gds.transaction.indicator.attachment_a11y'),
                file: t('gds.transaction.indicator.file_a11y'),
                location: t('gds.transaction.indicator.location_a11y'),
                additionalCard: t('gds.transaction.indicator.additionalCard_a11y'),
            },
        },
    };
}
